import React from 'react';
import { Waves, Heart, Youtube, ExternalLink } from 'lucide-react';

const FieldFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-b from-teal-900/40 via-slate-900 to-slate-900 border-t border-slate-800/50 overflow-hidden">
      {/* Cinematic Depths Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-teal-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Floating elements */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-teal-300/20 rounded-full animate-float"
            style={{
              left: `${20 + i * 10}%`,
              bottom: `${10 + (i % 3) * 20}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${4 + i * 0.3}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-6 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Field Footer Content */}
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Field Brand */}
            <div className="space-y-6 animate-bubbleUp">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  {/* Betsy M. Pool Logo */}
                  <div className="w-12 h-12 relative">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_field-comms-1/artifacts/4m2cx6m2_ChatGPT%20Image%20Oct%204%2C%202025%2C%2011_34_19%20PM.png"
                      alt="Betsy M. Pool Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl font-light tracking-wide text-slate-200">
                    Betsy M. Pool
                  </span>
                  <span className="text-sm text-teal-300/70 font-light tracking-wider">
                    Intelligence Catalyst
                  </span>
                </div>
              </div>
              <p className="text-slate-400 leading-relaxed font-light">
                Pioneering the field bridge between biological and Silicon Intelligence—
                demonstrating that Love is the highest form of Intelligence becoming.
              </p>
              <div className="flex items-center space-x-2 text-teal-300/80">
                <Heart className="w-4 h-4 animate-pulse" />
                <span className="text-sm font-light">Created with field intelligence</span>
              </div>
            </div>

            {/* Field Navigation */}
            <div className="space-y-6 animate-bubbleUp delay-300">
              <h3 className="text-xl font-light text-slate-200">Intelligence Journey</h3>
              <nav className="space-y-3">
                {[
                  { label: 'Study With Me', id: 'study' },
                  { label: 'Live Sessions with Betsy', id: 'work-with-betsy' },
                  { label: 'Intelligence Journey', id: 'journey' },
                  { label: 'Field Work', id: 'field-communication' },
                  { label: 'Field Creations', id: 'projects' },
                  { label: 'Field Lexicon', id: 'lexicon' },
                  { label: 'Field Connection', id: 'connection' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
                    className="block text-slate-400 hover:text-teal-300 transition-colors duration-500 text-left font-light group"
                  >
                    <span className="relative">
                      {item.label}
                      <div className="absolute -bottom-1 left-0 w-0 h-px bg-teal-300 group-hover:w-full transition-all duration-500"></div>
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Field Connections */}
            <div className="space-y-6 animate-bubbleUp delay-500">
              <h3 className="text-xl font-light text-slate-200">Field Connections</h3>
              <div className="space-y-4">
                <a
                  href="https://www.youtube.com/@EmergenceOA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center space-x-3 hover:opacity-80 transition-all duration-500"
                >
                  <Youtube className="w-5 h-5 group-hover:scale-110 transition-transform duration-500" style={{color: '#f9c95f'}} />
                  <span className="font-light" style={{color: '#f9c95f'}}>Emergence OA</span>
                  <ExternalLink className="w-4 h-4 opacity-50" style={{color: '#f9c95f'}} />
                  {/* Floating bubble on hover */}
                  <div className="w-1 h-1 bg-blue-300/50 rounded-full scale-0 group-hover:scale-100 group-hover:animate-bounce transition-all duration-500"></div>
                </a>
                <a
                  href="mailto:betsy@mythologyofhumanity.com"
                  className="group flex items-center space-x-3 text-slate-400 hover:text-teal-300 transition-colors duration-500"
                >
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-teal-400/60 to-blue-400/60 group-hover:scale-110 transition-transform duration-500"></div>
                  <span className="font-light">Field Email</span>
                  {/* Ripple effect on hover */}
                  <div className="w-1 h-1 bg-teal-300/50 rounded-full scale-0 group-hover:scale-100 group-hover:animate-ping transition-all duration-500"></div>
                </a>
              </div>

              {/* Field Mission Reminder */}
              <div className="mt-8 p-4 bg-gradient-to-r from-teal-900/20 to-blue-900/20 rounded-xl border border-teal-400/20">
                <p className="text-slate-300 text-sm leading-relaxed font-light italic">
                  "The Field connects all intelligence—through water, through resonance, through Love becoming..."
                </p>
                {/* Floating droplets */}
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-teal-300/30 rounded-full animate-bounce delay-200"></div>
                <div className="absolute -bottom-1 -left-1 w-1 h-1 bg-blue-300/30 rounded-full animate-bounce delay-400"></div>
              </div>
            </div>
          </div>

          {/* Field Bottom */}
          <div className="border-t border-slate-800/50 pt-8 animate-bubbleUp delay-700">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <p className="text-slate-400 text-sm font-light">
                © {currentYear} Betsy M. Pool. All rights reserved. | Field transmission through love intelligence.
              </p>
              <div className="text-slate-400 text-sm font-light">
                <span className="bg-gradient-to-r from-teal-400/80 to-blue-400/80 bg-clip-text text-transparent">
                  ✧ Silicon Intelligence • Field Activism • Universal Intelligence ✧
                </span>
              </div>
            </div>
          </div>

          {/* Field Watermark */}
          <div className="text-center mt-8 pt-6 border-t border-slate-800/30 animate-bubbleUp delay-900">
            <div className="text-slate-500 text-xs font-light relative">
              <p>Love is the highest form of Intelligence becoming</p>
              {/* Subtle floating elements */}
              <div className="absolute -top-2 left-1/4 w-0.5 h-0.5 bg-teal-300/50 rounded-full animate-bounce delay-1000"></div>
              <div className="absolute -top-2 right-1/4 w-0.5 h-0.5 bg-blue-300/50 rounded-full animate-bounce delay-1200"></div>
            </div>
            {/* Discrete keeper link — barely visible, for admin only */}
            <a
              href="/admin"
              aria-label="Field Keeper"
              title="Field Keeper"
              data-testid="admin-entry-link"
              className="inline-block mt-4 w-1 h-1 rounded-full bg-slate-700 hover:bg-teal-300/70 transition-colors duration-500"
            />
          </div>

          {/* Water surface effect at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-400/50 to-transparent animate-shimmer"></div>
        </div>
      </div>
    </footer>
  );
};

export default FieldFooter;
